import { Injectable } from "@angular/core";
import { CryptocoinService } from "./cryptocoin.service";
import { MetalService } from "./metal.service";
import { ListService } from "../list-service";
import { InitService } from "@core-services/init.service";
import { ServiceNames } from "@models/service_enums";
import { Tradable } from "@models/interfaces/tradable";
import { TradableService } from "./tradable.service";
import { IndexService } from "./index.service";
import { StockService } from "./stock.service";
import { EtfService } from "./etf.service";
import { DestructorService } from "@core-services/destructor.service";
import { EtcService } from "@main-services/currency/etc.service";
import { LeveragedTokenService } from "@main-services/currency/leveragedToken.service";
import { AssetDetailPriceChange } from "@modules/tradable/tradable-detail/domain/asset-detail-price-change.model";
import { FiatEarnService } from "@main-services/currency/fiat_earn.service";
import { AssetGroupName } from "@modules/+new/shared/const/enums/asset-group-name.enum";
import { Observable } from "rxjs";
import { SecurityTokenService } from "@main-services/currency/securityToken.service";

@Injectable({
	providedIn: "root"
})
export class TradableListService extends ListService<Tradable, TradableService<Tradable>> {
	public name: string = ServiceNames.TRADABLE_LIST;

	public constructor(
		protected initService: InitService,
		protected destructor: DestructorService,
		private coinService: CryptocoinService,
		private metalService: MetalService,
		private indexService: IndexService,
		private stockService: StockService,
		private etfService: EtfService,
		private etcService: EtcService,
		private leveragedTokenService: LeveragedTokenService,
		private fiatEarnService: FiatEarnService,
		private securityTokenService: SecurityTokenService
	) {
		super(initService, destructor, name);

		this.addSubForList([
			this.coinService,
			this.metalService,
			this.indexService,
			this.stockService,
			this.etfService,
			this.etcService,
			this.leveragedTokenService,
			this.fiatEarnService,
			this.securityTokenService
		]);
	}

	public findBySymbolOrName(name: string): Tradable {
		const item: Tradable = this.list.find((a) => a.getSymbol(true) === name.toLowerCase());
		if (item) {
			return item;
		}
		return this.list.find((a) => a.getName().toLowerCase() === name);
	}

	public serviceForCurrencyType(currencyType: string): TradableService<Tradable> {
		return this.services.find((s) => s.list.some((t) => t.getCurrencyType() === currencyType));
	}

	public getItemByPid(pid: string): Tradable {
		return this.list.find((m) => (!m ? false : m.getPid() === pid));
	}

	public getCashPlusAssets(): Tradable[] {
		return this.list.filter((cashPlus) => cashPlus.getAssetGroupName() === AssetGroupName.FiatEarn);
	}

	public getTradables(): Tradable[] {
		return this.list;
	}

	public getTradablesObservable$: Observable<Tradable[]> = this._list.asObservable();

	// new function used by ADP v3/assets/details/PID to update the price and percent //new AssetPrice()

	public updatePrice(pid: string, priceUpdates: AssetDetailPriceChange): void {
		// update tradable by reference
		const t = this.getItemByPid(pid);
		t.getAssetPrices().setPrice(priceUpdates.price);
		t.getAssetPrices().setChange24h(priceUpdates.price_change.day.percent);
		t.getAssetPrices().setChange24hAmount(priceUpdates.price_change.day.amount);
		t.getAssetPrices().setChange1w(priceUpdates.price_change.week.percent);
		t.getAssetPrices().setChange1wAmount(priceUpdates.price_change.week.amount);
		t.getAssetPrices().setChange1m(priceUpdates.price_change.month.percent);
		t.getAssetPrices().setChange1mAmount(priceUpdates.price_change.month.amount);
		t.getAssetPrices().setChange6m(priceUpdates.price_change.six_months.percent);
		t.getAssetPrices().setChange6mAmount(priceUpdates.price_change.six_months.amount);
		t.getAssetPrices().setChange5y(priceUpdates.price_change.five_years.percent);
		t.getAssetPrices().setChange5yAmount(priceUpdates.price_change.five_years.amount);

		// then re-emit from behavior subject to notify subscribers of the change
		this._list.next(this.list);
	}
}
